import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  openingBalances,
  type OpeningBalanceItem,
} from '@/api/openingBalances';
import { orgs, contractors as contractorsApi, nomenclature as nomApi } from '@/api/catalogs';
import { refs } from '@/api/refs';
import { useConfirm } from '@/components/ConfirmProvider';
import DatePopover from '@/components/DatePopover';
import { useNumberPreview } from '@/hooks/useNumberPreview';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import DocumentToolbar from '@/shared/components/DocumentToolbar';

interface FormState {
  organization_id: string;
  document_number: string;
  document_date: string;
  comment: string;
  items: OpeningBalanceItem[];
}

const EMPTY_FORM: FormState = {
  organization_id: '',
  document_number: '',
  document_date: new Date().toISOString().slice(0, 16),
  comment: '',
  items: [],
};

const newRow = (n: number): OpeningBalanceItem => ({
  line_number: n,
  account_code: '',
  debit_amount: '0',
  credit_amount: '0',
});

const fmt = (n: number) =>
  n.toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Підказки субконто за кодом рахунку (типові для українського обліку)
const SUBCONTO_HINT: Record<string, { type: string; label: string }[]> = {
  '361': [{ type: 'ref.contractors', label: 'Контрагент' }, { type: 'ref.contracts', label: 'Договір' }],
  '362': [{ type: 'ref.contractors', label: 'Контрагент' }, { type: 'ref.contracts', label: 'Договір' }],
  '377': [{ type: 'ref.contractors', label: 'Контрагент' }, { type: 'ref.contracts', label: 'Договір' }],
  '631': [{ type: 'ref.contractors', label: 'Контрагент' }, { type: 'ref.contracts', label: 'Договір' }],
  '632': [{ type: 'ref.contractors', label: 'Контрагент' }, { type: 'ref.contracts', label: 'Договір' }],
  '685': [{ type: 'ref.contractors', label: 'Контрагент' }, { type: 'ref.contracts', label: 'Договір' }],
  '281': [{ type: 'ref.nomenclature', label: 'Номенклатура' }],
  '3721': [{ type: 'ref.employees', label: 'Співробітник' }],
  '3722': [{ type: 'ref.employees', label: 'Співробітник' }],
  '661': [{ type: 'ref.employees', label: 'Співробітник' }],
  '662': [{ type: 'ref.employees', label: 'Співробітник' }],
};

export default function OpeningBalanceEditor() {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  // UX β: фільтр org dropdown по admin.settings.manage permission
  const {
    availableOrgs,
    hasPermissionInAnyOrg,
    totalAccessibleOrgs,
    loading: permsLoading,
  } = usePermissionsByOrg('admin.settings.manage');
  const ctrQ = useQuery({
    queryKey: ['contractors-all'],
    queryFn: () => contractorsApi.list({ limit: 2000 }),
  });
  const nomQ = useQuery({
    queryKey: ['nomenclature-full'],
    queryFn: () => nomApi.list({ limit: 2000 }),
  });
  const empQ = useQuery({
    queryKey: ['employees-all-opening', /* organization */],
    queryFn: () => refs.employees(),
  });
  const docQuery = useQuery({
    queryKey: ['opening-balance', id],
    queryFn: () => openingBalances.get(id!),
    enabled: !isNew,
  });

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const numberPreview = useNumberPreview('doc.opening_balances', form.organization_id, isNew);
  const [error, setError] = useState<string | null>(null);

  const warehousesQ = useQuery({
    queryKey: ['warehouses', form.organization_id],
    queryFn: () => refs.warehouses(form.organization_id),
    enabled: !!form.organization_id,
  });

  useEffect(() => {
    if (isNew) {
      const firstOrg = availableOrgs[0]?.id ?? '';
      setForm((f) => f.organization_id ? f : {
        ...f, organization_id: firstOrg,
        items: f.items.length ? f.items : [newRow(1)],
      });
    } else if (docQuery.data) {
      const d = docQuery.data;
      setForm({
        organization_id: d.organization_id,
        document_number: d.document_number,
        document_date: d.document_date.slice(0, 16),
        comment: d.comment ?? '',
        items: d.items.map((it) => ({
          line_number: it.line_number,
          account_code: it.account_code,
          subconto_1_type: it.subconto_1_type,
          subconto_1_id: it.subconto_1_id,
          subconto_2_type: it.subconto_2_type,
          subconto_2_id: it.subconto_2_id,
          subconto_3_type: it.subconto_3_type,
          subconto_3_id: it.subconto_3_id,
          debit_amount: String(it.debit_amount),
          credit_amount: String(it.credit_amount),
          warehouse_id: it.warehouse_id,
          quantity: it.quantity ? String(it.quantity) : null,
          comment: it.comment,
        })),
      });
    }
  }, [isNew, docQuery.data, availableOrgs]);

  const isPosted = !isNew && (docQuery.data?.posted ?? false);

  const totalDt = form.items.reduce((s, e) => s + Number(e.debit_amount || 0), 0);
  const totalCr = form.items.reduce((s, e) => s + Number(e.credit_amount || 0), 0);

  const create = useMutation({
    mutationFn: () => openingBalances.create({
      organization_id: form.organization_id,
      document_number: form.document_number,
      document_date: new Date(form.document_date).toISOString(),
      comment: form.comment || null,
      items: form.items,
    }),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['opening-balances'] });
      navigate(`/opening-balances/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  const update = useMutation({
    mutationFn: () => openingBalances.update(id!, {
      document_number: form.document_number,
      document_date: new Date(form.document_date).toISOString(),
      comment: form.comment || null,
      items: form.items,
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['opening-balance', id] }),
    onError: (e) => setError(extractError(e)),
  });
  const createAndPost = useMutation({
    mutationFn: async () => {
      const doc = await openingBalances.create({
        organization_id: form.organization_id,
        document_number: form.document_number,
        document_date: new Date(form.document_date).toISOString(),
        comment: form.comment || null,
        items: form.items,
      });
      await openingBalances.post(doc.id);
      return doc;
    },
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['opening-balances'] });
      navigate(`/opening-balances/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  const updateAndPost = useMutation({
    mutationFn: async () => {
      await openingBalances.update(id!, {
        document_number: form.document_number,
        document_date: new Date(form.document_date).toISOString(),
        comment: form.comment || null,
        items: form.items,
      });
      await openingBalances.post(id!);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['opening-balance', id] });
      qc.invalidateQueries({ queryKey: ['opening-balances'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const unpost = useMutation({
    mutationFn: () => openingBalances.unpost(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['opening-balance', id] });
      qc.invalidateQueries({ queryKey: ['opening-balances'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const remove = useMutation({
    mutationFn: () => openingBalances.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['opening-balances'] });
      navigate('/opening-balances');
    },
  });

  function setItem(i: number, patch: Partial<OpeningBalanceItem>) {
    setForm((f) => ({
      ...f,
      items: f.items.map((e, idx) => {
        if (idx !== i) return e;
        const next = { ...e, ...patch };
        // При зміні account_code скидаємо субконто (типи можуть не співпадати)
        if ('account_code' in patch) {
          next.subconto_1_id = null; next.subconto_1_type = null;
          next.subconto_2_id = null; next.subconto_2_type = null;
        }
        return next;
      }),
    }));
  }
  function addRow() {
    setForm((f) => ({ ...f, items: [...f.items, newRow(f.items.length + 1)] }));
  }
  function removeRow(i: number) {
    setForm((f) => ({
      ...f,
      items: f.items.filter((_, idx) => idx !== i)
        .map((e, idx) => ({ ...e, line_number: idx + 1 })),
    }));
  }

  // Опції для субконто за типом
  const subcontoOptions = useMemo(() => {
    const m: Record<string, { id: string; label: string }[]> = {
      'ref.contractors': (ctrQ.data?.items ?? []).map((c) => ({ id: c.id, label: c.name })),
      'ref.nomenclature': (nomQ.data?.items ?? []).map((n) => ({ id: n.id, label: n.name })),
      'ref.employees': (empQ.data ?? []).map((e) => ({
        id: e.id,
        label: e.code ? `${e.code} — ${e.name}` : e.name,
      })),
    };
    return m;
  }, [ctrQ.data, nomQ.data, empQ.data]);

  // UX β: warning якщо новий документ а user не має admin.settings.manage у жодній org
  if (isNew && !permsLoading && !hasPermissionInAnyOrg) {
    return (
      <div className="card">
        <div style={{ padding: '1.5rem', textAlign: 'center' }}>
          <div style={{
            padding: '1rem', background: '#fef3c7', borderRadius: 6,
            border: '1px solid #fbbf24', color: '#92400e',
            maxWidth: 600, margin: '1rem auto',
          }}>
            ⚠ У вас немає прав створювати введення залишків у жодній з ваших
            організацій ({totalAccessibleOrgs}). Зверніться до
            адміністратора tenant'а.
          </div>
          <button onClick={() => navigate('/opening-balances')}>Скасувати</button>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? 'Нові вступні залишки' : `Вступні залишки № ${form.document_number}`}
          {isPosted && (
            <span style={{ marginLeft: '0.75rem', color: '#15803d', fontSize: '1rem' }}>
              ✓ проведено
            </span>
          )}
        </h2>
        <DocumentToolbar
          onBackToList={() => navigate('/opening-balances')}
          onSave={() => isNew ? create.mutate() : update.mutate()}
          saveDisabled={create.isPending || update.isPending}
          isPosted={isPosted}
          isNew={isNew}
          onPost={() => isNew ? createAndPost.mutate() : updateAndPost.mutate()}
          postDisabled={!isNew && form.items.length === 0}
          postPending={create.isPending || createAndPost.isPending || updateAndPost.isPending || update.isPending}
          postTitle="Зберігає поточні зміни та проводить документ"
          onUnpost={() => unpost.mutate()}
          unpostPending={unpost.isPending}
          onDelete={() => { confirm('Видалити документ?').then((ok) => { if (ok) remove.mutate(); }) }}
          deletePending={remove.isPending}
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
        <div className="field">
          <label>Організація</label>
          <select value={form.organization_id}
                  onChange={(e) => setForm({ ...form, organization_id: e.target.value })}
                  disabled={!isNew}>
            <option value="">—</option>
            {(isNew ? availableOrgs : orgList.data?.items ?? []).map((o) => (
              <option key={o.id} value={o.id}>{o.code} — {o.name}</option>
            ))}
          </select>
          {isNew && availableOrgs.length < totalAccessibleOrgs && (
            <small style={{ color: '#64748b', fontSize: '0.75rem' }}>
              Показано {availableOrgs.length} з {totalAccessibleOrgs} ваших організацій
              (інші не дозволяють цю операцію)
            </small>
          )}
        </div>
        <div className="field">
          <label>№ документа</label>
          <input value={form.document_number}
                 onChange={(e) => setForm({ ...form, document_number: e.target.value })}
                 placeholder={isNew && numberPreview ? `буде: ${numberPreview}` : ''}
                 disabled={isPosted} />
        </div>
        <div className="field">
          <label>Дата залишків</label>
          <DatePopover withTime value={form.document_date}
                 onChange={(v) => setForm({ ...form, document_date: v })}
                 disabled={isPosted} />
        </div>
      </div>

      <h3 style={{ marginTop: '1rem' }}>Залишки за рахунками</h3>
      <p style={{ color: '#64748b', fontSize: '0.85rem', marginTop: '-0.3rem' }}>
        Для активних рахунків (281, 311, 361, 3721 тощо) — заповнюйте «Дебет».<br/>
        Для пасивних (631, 661, 6411) — «Кредит». Для 661 у субконто оберіть співробітника.<br/>
        Якщо ведете товарний залишок на 281 — додатково вкажіть склад і кількість.
      </p>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
        <thead>
          <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
            <th style={{ padding: '0.5rem', width: 30 }}>#</th>
            <th style={{ padding: '0.5rem', width: 80 }}>Рахунок</th>
            <th style={{ padding: '0.5rem' }}>Субконто 1</th>
            <th style={{ padding: '0.5rem', width: 130 }}>Дебет</th>
            <th style={{ padding: '0.5rem', width: 130 }}>Кредит</th>
            <th style={{ padding: '0.5rem', width: 130 }}>Склад / К-сть</th>
            <th style={{ padding: '0.5rem', width: 40 }}></th>
          </tr>
        </thead>
        <tbody>
          {form.items.map((it, i) => {
            const hints = SUBCONTO_HINT[it.account_code] ?? [];
            const sub1Type = hints[0]?.type ?? it.subconto_1_type ?? '';
            const sub1Opts = sub1Type ? (subcontoOptions[sub1Type] ?? []) : [];
            const isGoods = it.account_code.startsWith('281');
            return (
              <tr key={i} style={{ borderTop: '1px solid #e2e8f0' }}>
                <td style={{ padding: '0.5rem' }}>{it.line_number}</td>
                <td style={{ padding: '0.5rem' }}>
                  <input value={it.account_code}
                         onChange={(e) => setItem(i, { account_code: e.target.value })}
                         disabled={isPosted}
                         placeholder="361" style={{ width: '100%' }} />
                </td>
                <td style={{ padding: '0.5rem' }}>
                  {sub1Type ? (
                    <select value={it.subconto_1_id ?? ''}
                            onChange={(e) => setItem(i, {
                              subconto_1_id: e.target.value || null,
                              subconto_1_type: e.target.value ? sub1Type : null,
                            })}
                            disabled={isPosted}
                            style={{ width: '100%' }}>
                      <option value="">— {hints[0]?.label ?? ''} —</option>
                      {sub1Opts.map((o) => (
                        <option key={o.id} value={o.id}>{o.label}</option>
                      ))}
                    </select>
                  ) : (
                    <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>—</span>
                  )}
                </td>
                <td style={{ padding: '0.5rem' }}>
                  <input type="number" step="0.01" value={it.debit_amount}
                         onChange={(e) => setItem(i, { debit_amount: e.target.value })}
                         disabled={isPosted}
                         style={{ textAlign: 'right', width: '100%' }} />
                </td>
                <td style={{ padding: '0.5rem' }}>
                  <input type="number" step="0.01" value={it.credit_amount}
                         onChange={(e) => setItem(i, { credit_amount: e.target.value })}
                         disabled={isPosted}
                         style={{ textAlign: 'right', width: '100%' }} />
                </td>
                <td style={{ padding: '0.5rem' }}>
                  {isGoods ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                      <select value={it.warehouse_id ?? ''}
                              onChange={(e) => setItem(i, { warehouse_id: e.target.value || null })}
                              disabled={isPosted}
                              style={{ fontSize: '0.75rem' }}>
                        <option value="">— склад —</option>
                        {warehousesQ.data?.map((w) => (
                          <option key={w.id} value={w.id}>{w.name}</option>
                        ))}
                      </select>
                      <input type="number" step="0.001" value={it.quantity ?? ''}
                             onChange={(e) => setItem(i, { quantity: e.target.value || null })}
                             disabled={isPosted}
                             placeholder="к-сть"
                             style={{ textAlign: 'right', fontSize: '0.75rem' }} />
                    </div>
                  ) : (
                    <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>—</span>
                  )}
                </td>
                <td style={{ padding: '0.5rem' }}>
                  {!isPosted && (
                    <button onClick={() => removeRow(i)} className="danger"
                            style={{ padding: '0.3rem 0.6rem' }}>×</button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr style={{ borderTop: '2px solid #cbd5e1', fontWeight: 600 }}>
            <td colSpan={3} style={{ padding: '0.5rem', textAlign: 'right' }}>Усього:</td>
            <td style={{ padding: '0.5rem', textAlign: 'right' }}>{fmt(totalDt)}</td>
            <td style={{ padding: '0.5rem', textAlign: 'right' }}>{fmt(totalCr)}</td>
            <td colSpan={2}></td>
          </tr>
        </tfoot>
      </table>
      {!isPosted && (
        <button onClick={addRow} style={{ marginTop: '0.75rem' }}>+ Додати рядок</button>
      )}

      {totalDt !== totalCr && (
        <div style={{
          marginTop: '0.5rem', padding: '0.5rem', background: '#fef3c7',
          color: '#92400e', fontSize: '0.85rem',
        }}>
          ⚠ Дебет ({fmt(totalDt)}) ≠ Кредит ({fmt(totalCr)}).
          Різниця: <strong>{fmt(totalDt - totalCr)}</strong>.
          Перед проведенням баланс має зійтися.
        </div>
      )}

      <div className="field" style={{ marginTop: '1rem' }}>
        <label>Коментар</label>
        <input value={form.comment}
               onChange={(e) => setForm({ ...form, comment: e.target.value })}
               disabled={isPosted} />
      </div>

      {error && <div className="error">{error}</div>}
    </div>
  );
}

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as {
      response?: {
        data?: { detail?: string | Array<{ loc?: unknown[]; msg?: string }> }
      }
    }).response;
    const detail = r?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail) && detail.length > 0) {
      return detail.slice(0, 3).map((item) => {
        const field = Array.isArray(item.loc) && item.loc.length > 1
          ? String(item.loc[item.loc.length - 1]) : 'поле';
        return `${field}: ${item.msg ?? 'невідома помилка'}`;
      }).join('; ');
    }
    return 'Error';
  }
  return String(e);
}
